import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, CreditCard, Loader2, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { quote } from "@/lib/pricing";
import { PageHero } from "@/pages/PageHero";
import { img } from "@/lib/gallery";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const QTYS = ["1", "2", "3", "4+"];
const EMPTY = {
  last_name: "", first_name: "", company: "", email: "", phone: "", whatsapp: "", country: "", city: "", address: "", port: "",
  quantity: "3", bread_type: "", bread_weight: "", daily_volume: "", lpg: "unknown", water: "unknown", electricity: "unknown",
  desired_date: "", comments: "", accept_cgv: false, accept_privacy: false, accept_contact: false,
};
const REQUIRED = ["last_name", "first_name", "email", "phone", "country", "city", "port"];

const validate = (v, o) => {
  const e = {};
  REQUIRED.forEach((k) => { if (!String(v[k]).trim()) e[k] = o.required; });
  if (v.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = o.invalidEmail;
  if (v.phone && !/^[+()\d\s.-]{6,}$/.test(v.phone.trim())) e.phone = o.invalidPhone;
  ["accept_cgv", "accept_privacy", "accept_contact"].forEach((k) => { if (!v[k]) e[k] = o.mustAccept; });
  return e;
};

const Field = ({ name, label, v, set, err, type = "text", req, as, children, className = "" }) => {
  const Comp = as || "input";
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.12em] text-[var(--steel)]">{label}{req && <span className="text-[var(--gold)]"> *</span>}</span>
      <Comp name={name} type={as ? undefined : type} value={v[name]} onChange={(e) => set(name, e.target.value)} aria-invalid={!!err[name]}
        rows={as === "textarea" ? 4 : undefined} className="field mt-2" data-testid={`order-${name}`}>{children}</Comp>
      {err[name] && <span className="mt-1 block text-xs text-[#F59B9B]" data-testid={`order-error-${name}`}>{err[name]}</span>}
    </label>
  );
};

const Block = ({ n, title, children }) => (
  <fieldset className="border-t border-white/12 pt-8">
    <legend className="mb-6 flex items-center gap-3 pr-3 font-display text-2xl font-bold uppercase"><span className="font-mono text-xs text-[var(--gold)]">0{n}</span>{title}</legend>
    <div className="grid gap-5 sm:grid-cols-2">{children}</div>
  </fieldset>
);

const SellerIdentity = ({ seller }) => (
  <div className="mb-12 border border-[var(--gold)]/50 bg-[#0B1530] p-6 sm:p-8" data-testid="order-seller-identity">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">MB300 · by NELVIS FRANCE</p>
    <h2 className="font-display mt-4 text-2xl font-bold uppercase text-white">{seller.title}</h2>
    <div className="mt-5 grid gap-2 text-sm text-[var(--steel)] sm:grid-cols-2">
      <p data-testid="order-seller-legal-name"><strong className="text-white">{seller.legalName}</strong><br />{seller.legalForm}</p>
      <p data-testid="order-seller-registration">{seller.siren}<br />{seller.rcs}</p>
      <p data-testid="order-seller-address">{seller.addressLabel}<br />50 rue Pelleport<br />75020 Paris<br />France</p>
      <p data-testid="order-seller-commercial-activity">{seller.activityLabel}<br /><strong className="text-white">MB300 by NELVIS FRANCE</strong></p>
    </div>
  </div>
);

const Summary = ({ qty }) => {
  const { t, eur } = useI18n();
  const o = t.order;
  const q = quote(qty === "4+" ? 4 : Number(qty));
  const Line = ({ l, v, big, id }) => (
    <div className="flex items-baseline justify-between gap-4 border-t border-white/10 py-3">
      <span className="text-sm text-[var(--steel)]">{l}</span>
      <span className={big ? "font-display text-4xl font-black text-[var(--gold)]" : "font-semibold"} data-testid={`summary-${id}`}>{v}</span>
    </div>
  );
  return (
    <div className="border border-white/12 bg-[#0B1530] p-6" data-testid="order-summary">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{o.summary} · {o.qtyOpts[qty]}</p>
      {q ? (
        <div className="mt-4">
          <Line l={`${o.total} ${t.common.ht}`} v={eur(q.total)} big id="total" />
          <Line l={o.unitPrice} v={eur(q.unit)} id="unit" />
          {q.saving > 0 && <Line l={o.saving} v={eur(q.saving)} id="saving" />}
          <Line l={o.deposit} v={eur(q.deposit)} id="deposit" />
          <Line l={o.balance} v={eur(q.balance)} id="balance" />
        </div>
      ) : (
        <p className="mt-4 text-sm leading-relaxed text-white" data-testid="summary-network">{o.network}</p>
      )}
      <p className="mt-5 flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" />{o.included}</p>
      <p className="mt-2 text-xs text-[var(--steel-dim)]">{o.leadNote}</p>
      <p className="mt-3 border-l-2 border-[var(--gold)] pl-3 text-xs leading-relaxed text-[var(--steel)]" data-testid="order-energy-note">{o.energyNote}</p>
      <div className="mt-5 border-t border-white/10 pt-4" data-testid="order-exclusions">
        <p className="text-xs font-semibold text-[var(--steel)]">{o.excludedTitle}</p>
        <ul className="mt-2 space-y-1 text-xs text-[var(--steel-dim)]">
          {t.delivery.excl.map((x) => <li key={x} className="flex gap-2"><X className="mt-0.5 h-3 w-3 shrink-0" />{x}</li>)}
        </ul>
      </div>
      <p className="mt-4 text-[11px] text-[var(--steel-dim)]">{t.pricing.volumeCond}</p>
    </div>
  );
};

const Consent = ({ name, label, v, set, err, to }) => (
  <label className="flex cursor-pointer items-start gap-3">
    <input type="checkbox" checked={v[name]} onChange={(e) => set(name, e.target.checked)} className="mt-1 h-4 w-4 accent-[#C9A55C]" data-testid={`order-${name}`} />
    <span className="text-sm">
      {to ? <Link to={to} target="_blank" className="underline underline-offset-4 hover:text-[var(--gold)]">{label}</Link> : label}
      <span className="text-[var(--gold)]"> *</span>
      {err[name] && <span className="block text-xs text-[#F59B9B]" data-testid={`order-error-${name}`}>{err[name]}</span>}
    </span>
  </label>
);

export default function Order() {
  const { t, lang, eur } = useI18n();
  const o = t.order;
  const [params] = useSearchParams();
  const [v, setV] = useState(EMPTY);
  const [err, setErr] = useState({});
  const [status, setStatus] = useState("idle");
  const [done, setDone] = useState(null);
  const [paymentState, setPaymentState] = useState("idle");

  useEffect(() => {
    const q = params.get("qty");
    if (QTYS.includes(q)) setV((s) => ({ ...s, quantity: q }));
  }, [params]);

  const set = (k, val) => {
    setV((s) => ({ ...s, [k]: val }));
    setErr((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errors = validate(v, o);
    setErr(errors);
    if (Object.keys(errors).length) { setStatus("invalid"); return; }
    if (!API) { setStatus("error"); return; }
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/orders`, { ...v, email: v.email.trim(), lang });
      setDone({ reference: res.data?.reference ?? "—", confirmationSent: !!res.data?.confirmation_email_sent, pricing: res.data?.pricing });
      setStatus("idle");
      window.scrollTo(0, 0);
    } catch {
      setStatus("error");
    }
  };

  const startPayment = async (provider) => {
    setPaymentState(provider);
    try {
      const path = provider === "mollie" ? "payments/mollie/checkout" : "payments/checkout";
      const res = await axios.post(`${API}/${path}`, {
        order_reference: done.reference,
        ...(provider === "mollie" ? { lang } : { origin_url: window.location.origin }),
      }, { withCredentials: true });
      window.location.assign(res.data.checkout_url);
    } catch {
      setPaymentState("error");
    }
  };

  const f = (name, p = {}) => <Field key={name} name={name} v={v} set={set} err={err} label={o.f[name]} {...p} />;
  const avail = (name) => f(name, { as: "select", children: ["yes", "partial", "no", "unknown"].map((k) => <option key={k} value={k}>{o.avail[k]}</option>) });

  return (
    <div data-testid="order-page">
      <PageHero eyebrow="MB300 · by NELVIS FRANCE" title={o.title} text={o.intro} image={img(12)} />
      <section className="container-x pb-28">
        <SellerIdentity seller={o.seller} />
        {done ? (
          <div className="mx-auto max-w-2xl border border-[var(--gold)] p-10 text-center" data-testid="order-success">
            <Check className="mx-auto h-10 w-10 text-[var(--gold)]" />
            <p className="font-display mt-4 text-4xl font-black uppercase">{o.successT}</p>
            <p className="mt-3 text-[var(--steel)]">{o.successD}</p>
            <p className="mt-2 font-mono text-xl text-[var(--gold)]" data-testid="order-reference">{done.reference}</p>
            <p className="mt-4 text-sm text-white" data-testid="order-confirmation-email-status">
              {done.confirmationSent ? o.successEmail : o.successEmailPending}
            </p>
            <p className="mt-4 text-sm text-[var(--steel)]">{o.successNext}</p>
            {done.pricing && (
              <div className="mt-8 border-t border-white/15 pt-8" data-testid="order-payment-actions">
                <button type="button" onClick={() => startPayment("mollie")} disabled={["mollie", "stripe"].includes(paymentState)} data-testid="order-pay-mollie"
                  className="mx-auto flex w-full items-center justify-center gap-3 bg-[var(--gold)] px-6 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[var(--night)] disabled:opacity-60 sm:w-auto">
                  {paymentState === "mollie" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {paymentState === "mollie" ? o.paymentStarting : `${o.payMollie} · ${eur(done.pricing.deposit)}`}
                </button>
                <p className="mt-3 text-xs text-[var(--steel-dim)]" data-testid="order-mollie-secure-note">{o.secureMollie}</p>
                <button type="button" onClick={() => startPayment("stripe")} disabled={["mollie", "stripe"].includes(paymentState)} data-testid="order-pay-stripe"
                  className="mx-auto mt-5 flex items-center justify-center gap-2 border-b border-white/30 pb-1 text-xs uppercase tracking-[0.12em] text-[var(--steel)] hover:text-white disabled:opacity-60">
                  {paymentState === "stripe" && <Loader2 className="h-3 w-3 animate-spin" />}{o.payStripe}
                </button>
                {paymentState === "error" && <p className="mt-3 text-sm text-[#F59B9B]" data-testid="order-payment-error">{o.paymentError}</p>}
              </div>
            )}
            <button onClick={() => { setDone(null); setV(EMPTY); }} className="mt-8 border border-white/30 px-6 py-3 text-xs uppercase tracking-[0.16em]" data-testid="order-new">{o.newOrder}</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="grid gap-12 lg:grid-cols-12" data-testid="order-form">
            <div className="space-y-12 lg:col-span-7">
              <fieldset className="pt-2">
                <legend className="mb-5 flex items-center gap-3 font-display text-2xl font-bold uppercase"><span className="font-mono text-xs text-[var(--gold)]">00</span>{o.f.quantity}</legend>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="radiogroup">
                  {QTYS.map((q) => (
                    <button type="button" key={q} onClick={() => set("quantity", q)} data-testid={`order-qty-${q === "4+" ? "more" : q}`} aria-pressed={v.quantity === q}
                      className={`border px-4 py-4 text-left transition-colors ${v.quantity === q ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-white/15 hover:border-white/40"}`}>
                      <span className="font-display block text-3xl font-black">{q === "4+" ? "3+" : q}</span>
                      <span className="text-xs text-[var(--steel)]">{o.qtyOpts[q]}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
              <Block n={1} title={o.s1}>
                {f("last_name", { req: true })}{f("first_name", { req: true })}{f("company", { className: "sm:col-span-2" })}
                {f("email", { type: "email", req: true })}{f("phone", { type: "tel", req: true })}{f("whatsapp", { type: "tel" })}
              </Block>
              <Block n={2} title={o.s2}>
                {f("country", { req: true })}{f("city", { req: true })}{f("address", { className: "sm:col-span-2" })}{f("port", { req: true, className: "sm:col-span-2" })}
              </Block>
              <Block n={3} title={o.s3}>
                {f("bread_type")}{f("bread_weight", { type: "number" })}{f("daily_volume", { type: "number" })}{f("desired_date", { type: "date" })}
                {avail("lpg")}{avail("water")}{avail("electricity")}
                {f("comments", { as: "textarea", className: "sm:col-span-2" })}
              </Block>
              <fieldset className="space-y-4 border-t border-white/12 pt-8">
                <legend className="mb-6 flex items-center gap-3 pr-3 font-display text-2xl font-bold uppercase"><span className="font-mono text-xs text-[var(--gold)]">04</span>{o.s5}</legend>
                <Consent name="accept_cgv" label={o.c1} to="/cgv" v={v} set={set} err={err} />
                <Consent name="accept_privacy" label={o.c2} to="/confidentialite" v={v} set={set} err={err} />
                <Consent name="accept_contact" label={o.c3} v={v} set={set} err={err} />
              </fieldset>
              {status === "invalid" && <p className="text-sm text-[#F59B9B]" data-testid="order-form-invalid">{o.fixErrors}</p>}
              {status === "error" && <p className="text-sm text-[#F59B9B]" data-testid="order-form-error">{o.error}</p>}
              <button type="submit" disabled={status === "sending"} data-testid="order-submit" className="flex w-full items-center justify-center gap-3 bg-[var(--gold)] px-8 py-5 text-sm font-bold uppercase tracking-[0.18em] text-[var(--night)] transition-colors hover:bg-[var(--gold-hi)] disabled:opacity-60 sm:w-auto">
                {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}{status === "sending" ? o.sending : o.submit}
              </button>
            </div>
            <aside className="order-first lg:order-none lg:col-span-5"><div className="lg:sticky lg:top-24"><Summary qty={v.quantity} /></div></aside>
          </form>
        )}
      </section>
    </div>
  );
}
